import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowRight, Home, Building, HomeIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to extract year from project description
const extractYear = (description) => {
  if (!description) return 0;
  const match = description.match(/Project Year:\s*(\d{4})/);
  return match ? parseInt(match[1], 10) : 0;
};

// Sort projects by year in descending order (newest first)
const sortByYearDesc = (projects) => {
  return [...projects].sort((a, b) => {
    const yearA = extractYear(a.description);
    const yearB = extractYear(b.description);
    return yearB - yearA;
  });
};

const Projects = () => {
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setFilter(categoryFromUrl.toLowerCase());
    }
    fetchProjects();
  }, [searchParams]);

  useEffect(() => {
    if (filter === 'all') {
      setFilteredProjects(sortByYearDesc(projects));
    } else {
      const filtered = projects.filter(p => p.type.toLowerCase() === filter);
      setFilteredProjects(sortByYearDesc(filtered));
    }
  }, [filter, projects]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const sortedProjects = sortByYearDesc(response.data);
      setProjects(sortedProjects);
      setFilteredProjects(sortedProjects);
    } catch (error) {
    }
  };

  const filters = [
    { value: 'all', label: 'All Projects', icon: Building },
    { value: 'plotting', label: 'Plotting Projects', icon: Home },
    { value: 'residential', label: 'Residential Projects', icon: HomeIcon },
    { value: 'commercial', label: 'Commercial Projects', icon: Building }
  ];

  return (
    <div data-testid="projects-page">
      <section
        className="relative h-[50vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1710746055183-ba55e616bfe8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxza3lzY3JhcGVyJTIwY29uc3RydWN0aW9uJTIwc2l0ZSUyMHN1bnNldHxlbnwwfHx8fDE3NzExMzE1Njd8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-stone-900/60" />
        <div className="relative z-10 text-center px-6 text-white">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold tracking-tight mb-4">
            Our Projects
          </h1>
          <p className="text-xl text-stone-200">Explore Our Portfolio of Excellence</p>
        </div>
      </section>

      <section className="py-20 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="flex flex-wrap gap-4 mb-12 justify-center" data-testid="project-filters">
          {filters.map((f) => {
            const Icon = f.icon;
            return (
              <Button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={`filter-${f.value}`}
                variant={filter === f.value ? 'default' : 'outline'}
                className={`px-6 py-3 uppercase tracking-widest text-xs font-bold transition-all ${
                  filter === f.value
                    ? 'bg-emerald-900 text-white hover:bg-emerald-800'
                    : 'border-stone-300 text-stone-700 hover:border-emerald-900 hover:text-emerald-900'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {f.label}
              </Button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="projects-grid">
          {filteredProjects.map((project) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              data-testid={`project-card-${project.id}`}
              className="group relative overflow-hidden bg-white border border-stone-200 hover:border-amber-600/50 transition-colors duration-300"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img
                  src={project.image_url}
                  alt={project.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 right-4 bg-emerald-900 text-white px-3 py-1 text-xs uppercase tracking-widest">
                  {project.status}
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs text-stone-500 uppercase tracking-widest mb-2">
                  <MapPin className="w-3 h-3" />
                  {project.location}
                </div>
                <h3 className="text-2xl font-playfair font-bold text-stone-900 mb-2">{project.title}</h3>
                <p className="text-stone-600 line-clamp-2 mb-4">{project.description}</p>
                {project.price_range && (
                  <p className="text-amber-600 font-bold mb-4">{project.price_range}</p>
                )}
                <span className="text-emerald-900 uppercase tracking-widest text-xs font-bold group-hover:gap-2 inline-flex items-center gap-1 transition-all">
                  VIEW DETAILS <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-20">
            <Building className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500 text-lg">No projects found in this category.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default Projects;